import { useState, type FormEvent } from "react";
import { Fingerprint } from "lucide-react";
import { Button } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { addPasskey, isCancelled, loginWithPasskey, passkeysSupported } from "../lib/passkeys";

export function LoginScreen({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [offerPasskey, setOfferPasskey] = useState(false);
  const supported = passkeysSupported();

  async function withPasskey() {
    setError(null);
    setBusy(true);
    try {
      await loginWithPasskey();
      onLoggedIn();
    } catch (e) {
      if (!isCancelled(e)) setError(e instanceof ApiError ? e.message : "Inloggen met je passkey is niet gelukt. Gebruik je wachtwoord.");
    } finally {
      setBusy(false);
    }
  }

  async function withPassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api("/auth/login", { body: { email, password } });
      if (supported) setOfferPasskey(true);
      else onLoggedIn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Geen verbinding. Probeer het opnieuw zodra je online bent.");
    } finally {
      setBusy(false);
    }
  }

  async function createPasskey() {
    setError(null);
    setBusy(true);
    try {
      await addPasskey();
      onLoggedIn();
    } catch (e) {
      if (isCancelled(e)) onLoggedIn();
      else setError(e instanceof ApiError ? e.message : "De passkey is niet opgeslagen. Je kunt het later opnieuw proberen via Meer.");
    } finally {
      setBusy(false);
    }
  }

  if (offerPasskey) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-5 px-4">
        <Fingerprint aria-hidden className="size-12 text-pine" />
        <h1 className="text-2xl font-bold">Voortaan inloggen met je vingerafdruk?</h1>
        <p className="text-ink-soft">
          Met een passkey log je in met je vingerafdruk of schermvergrendeling. Je wachtwoord blijft werken als terugval.
        </p>
        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        <Button onClick={createPasskey} disabled={busy}>
          Passkey toevoegen
        </Button>
        <Button variant="secondary" onClick={onLoggedIn} disabled={busy}>
          Later
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
      <h1 className="mb-1 text-3xl font-bold">Leven</h1>
      <p className="mb-8 text-ink-soft">Log in om je dag te zien.</p>
      {supported && (
        <>
          <Button onClick={withPasskey} disabled={busy}>
            <Fingerprint aria-hidden className="size-5" />
            Inloggen met passkey
          </Button>
          <p className="my-6 text-center text-ink-soft">of met je wachtwoord</p>
        </>
      )}
      <form onSubmit={withPassword} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="font-semibold">E mailadres</span>
          <input
            type="email"
            autoComplete="username webauthn"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-h-12 rounded-xl border-2 border-mist bg-surface px-3 text-ink"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-semibold">Wachtwoord</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="min-h-12 rounded-xl border-2 border-mist bg-surface px-3 text-ink"
          />
        </label>
        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        <Button type="submit" variant={supported ? "secondary" : "primary"} disabled={busy}>
          Inloggen
        </Button>
      </form>
    </main>
  );
}
